import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import Login from './pages/auth/Login'
import Dashboard from './pages/dashboard/Dashboard'
import Layout from './components/layout/Layout'

// Veículos
import { VeiculosList, VeiculoForm, VeiculoDetail } from './pages/veiculos'

// Clientes
import { ClientesList, ClienteForm } from './pages/clientes'

// Fornecedores
import { FornecedoresList, FornecedorForm } from './pages/fornecedores'

// CRM
import { LeadsKanban, LeadForm, TarefasList, TarefaForm, CrmDashboard } from './pages/crm'

// Agenda
import { Agenda } from './pages/agenda'

// Financeiro
import { Financeiro, ContasAPagar, ContasAReceber, ContasBancarias, FluxoCaixa } from './pages/financeiro'

// Vendas
import { VendasList, VendaForm } from './pages/vendas'

// Comercial
import { SimuladorFinanciamento } from './pages/comercial'

// Integrador (legacy - removido, usa redirect para novo módulo)

// Integrações (novo módulo)
import {
  IntegracoesDashboard,
  PlataformasList,
  PlataformaConfig,
  PublicacoesGerenciamento,
  PublicacaoDetalhe,
  SincronizacaoView,
  XmlFeedConfig
} from './pages/integracoes'

// Relatórios
import { Relatorios } from './pages/relatorios'

// Perfil e Configurações
import { Perfil } from './pages/perfil'
import { Configuracoes } from './pages/configuracoes'
import WhatsAppConfig from './pages/configuracoes/WhatsAppConfig'
import RevendaMaisConfig from './pages/configuracoes/RevendaMaisConfig'
import { SiteConfig } from './pages/configuracoes'

// Chat
import ChatAtendimento from './pages/chat/ChatAtendimento'

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />
}

// Componente para proteger rotas por perfil de usuário
interface RoleRouteProps {
  children: React.ReactNode
  allowedRoles: ('ADMIN' | 'GERENTE' | 'VENDEDOR')[]
  redirectTo?: string
}

function RoleRoute({ children, allowedRoles, redirectTo = '/dashboard' }: RoleRouteProps) {
  const { user } = useAuth()

  if (!user?.tipoUsuario || !allowedRoles.includes(user.tipoUsuario as 'ADMIN' | 'GERENTE' | 'VENDEDOR')) {
    return <Navigate to={redirectTo} replace />
  }

  return <>{children}</>
}

// Atalho para rotas apenas de administradores
function AdminRoute({ children }: { children: React.ReactNode }) {
  return <RoleRoute allowedRoles={['ADMIN']}>{children}</RoleRoute>
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />

      <Route
        path="/*"
        element={
          <PrivateRoute>
            <Layout>
              <Routes>
                {/* Dashboard */}
                <Route path="/" element={<Dashboard />} />
                <Route path="/dashboard" element={<Dashboard />} />

                {/* Veículos */}
                <Route path="/veiculos" element={<VeiculosList />} />
                <Route path="/veiculos/novo" element={<VeiculoForm />} />
                <Route path="/veiculos/:id" element={<VeiculoDetail />} />
                <Route path="/veiculos/:id/editar" element={<VeiculoForm />} />

                {/* Clientes */}
                <Route path="/clientes" element={<ClientesList />} />
                <Route path="/clientes/novo" element={<ClienteForm />} />
                <Route path="/clientes/:id" element={<ClienteForm />} />

                {/* Fornecedores */}
                <Route path="/fornecedores" element={<FornecedoresList />} />
                <Route path="/fornecedores/novo" element={<FornecedorForm />} />
                <Route path="/fornecedores/:id" element={<FornecedorForm />} />

                {/* CRM */}
                <Route path="/crm" element={<CrmDashboard />} />
                <Route path="/crm/leads" element={<LeadsKanban />} />
                <Route path="/crm/leads/novo" element={<LeadForm />} />
                <Route path="/crm/leads/:id" element={<LeadForm />} />
                <Route path="/crm/tarefas" element={<TarefasList />} />
                <Route path="/crm/tarefas/nova" element={<TarefaForm />} />
                <Route path="/crm/tarefas/:id" element={<TarefaForm />} />

                {/* Vendas */}
                <Route path="/vendas" element={<VendasList />} />
                <Route path="/vendas/nova" element={<VendaForm />} />
                <Route path="/vendas/:id" element={<VendaForm />} />

                {/* Comercial */}
                <Route path="/simulador-financiamento" element={<SimuladorFinanciamento />} />

                {/* Agenda */}
                <Route path="/agenda" element={<Agenda />} />

                {/* Financeiro - Apenas Administradores */}
                <Route path="/financeiro" element={<AdminRoute><Financeiro /></AdminRoute>} />
                <Route path="/financeiro/contas-pagar" element={<AdminRoute><ContasAPagar /></AdminRoute>} />
                <Route path="/financeiro/contas-receber" element={<AdminRoute><ContasAReceber /></AdminRoute>} />
                <Route path="/financeiro/contas-bancarias" element={<AdminRoute><ContasBancarias /></AdminRoute>} />
                <Route path="/financeiro/fluxo-caixa" element={<AdminRoute><FluxoCaixa /></AdminRoute>} />

                {/* Integrador (legacy - redireciona para novo módulo) */}
                <Route path="/integrador" element={<Navigate to="/integracoes/publicacoes" replace />} />

                {/* Chat ao Vivo */}
                <Route path="/chat" element={<ChatAtendimento />} />

                {/* Integrações */}
                <Route path="/integracoes" element={<IntegracoesDashboard />} />
                <Route path="/integracoes/plataformas" element={<PlataformasList />} />
                <Route path="/integracoes/plataformas/:portal" element={<PlataformaConfig />} />
                <Route path="/integracoes/publicacoes" element={<PublicacoesGerenciamento />} />
                <Route path="/integracoes/publicacoes/:veiculoId/:portal" element={<PublicacaoDetalhe />} />
                <Route path="/integracoes/sincronizacao" element={<SincronizacaoView />} />
                <Route path="/integracoes/xml-feed" element={<XmlFeedConfig />} />

                {/* Relatórios */}
                <Route path="/relatorios" element={<Relatorios />} />

                {/* Perfil e Configurações */}
                <Route path="/perfil" element={<Perfil />} />
                <Route path="/configuracoes" element={<Configuracoes />} />
                <Route path="/configuracoes/whatsapp" element={<WhatsAppConfig />} />
                <Route path="/configuracoes/revendamais" element={<RevendaMaisConfig />} />
                <Route path="/configuracoes/site" element={<SiteConfig />} />

                {/* Catch-all redirect */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Layout>
          </PrivateRoute>
        }
      />
    </Routes>
  )
}

export default App
